import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { LifecycleFocus, weightUri } from '../lifecycle';
import { Badge, Button, Card, EmptyState, Field, inputStyle, tokens } from './ui/controls';

type Prediction = {
  class_name?: string;
  confidence?: number;
  bbox?: { x1: number; y1: number; x2: number; y2: number } | null;
};

type RegistryVersion = { version: string; tags: Record<string, string> };
type RegistryModel = { name: string; versions: RegistryVersion[] };

export default function InferenceTab({
  focus,
  onFocusConsumed,
}: {
  focus: LifecycleFocus | null;
  onFocusConsumed: () => void;
}) {
  const [models, setModels] = useState<RegistryModel[]>([]);
  const [query, setQuery] = useState('');
  const [modelUri, setModelUri] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [datasets, setDatasets] = useState<Array<{ dataset_id: string; name: string; version: string }>>([]);
  const [datasetId, setDatasetId] = useState('');
  const [images, setImages] = useState<Array<{ file_location: string; preview?: string }>>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.dataviewer.modelRegistry()
      .then((res) => setModels((res.data as { models: RegistryModel[] }).models || []))
      .catch((e) => setMessage(String(e)));
    api.dataviewer.listRegistryDatasets()
      .then((res) => {
        const listed = res.data.datasets || [];
        setDatasets((rows) => {
          const ids = new Set(listed.map((row) => row.dataset_id));
          return [...listed, ...rows.filter((row) => !ids.has(row.dataset_id))];
        });
      })
      .catch(() => setDatasets([]));
  }, []);

  const chooseVersion = useCallback((model: RegistryModel, version: RegistryVersion) => {
    const uri = weightUri(model.name, version.tags);
    setSelectedModel(`${model.name} v${version.version}`);
    setModelUri(uri);
    setPredictions([]);
    if (!uri) setMessage(`${model.name} v${version.version} has no S3 weight tag`);
    else setMessage('');
  }, []);

  const showImages = useCallback(async (locations: string[]) => {
    const signed: Record<string, string> = {};
    if (locations.length > 0) {
      const page = await api.dataviewer.getPresignedUrls(locations);
      for (const item of page.data.urls) {
        if (item.presigned_url) signed[item.s3_url] = item.presigned_url;
      }
    }
    setImages(locations.map((file_location) => ({ file_location, preview: signed[file_location] })));
    setSelected(null);
    setPreview(null);
    setPredictions([]);
  }, []);

  useEffect(() => {
    if (!focus || focus.tab !== 'inference') return;
    if ('datasetName' in focus) {
      const { datasetName, datasetVersion } = focus;
      const id = `${datasetName}@${datasetVersion}`;
      setDatasetId(id);
      setDatasets((rows) => (
        rows.some((row) => row.dataset_id === id)
          ? rows
          : [...rows, { dataset_id: id, name: datasetName, version: datasetVersion }]
      ));
      onFocusConsumed();
      api.dataviewer.listRegistryImages(datasetName, datasetVersion)
        .then((res) => showImages(res.data.images || []))
        .catch((e) => setMessage(String(e)));
      return;
    }
    setQuery(focus.modelName);
    const model = models.find((row) => row.name === focus.modelName);
    const version = model?.versions.find((row) => String(row.version) === String(focus.version));
    if (model && version) chooseVersion(model, version);
    if (models.length > 0) onFocusConsumed();
  }, [focus, models, chooseVersion, onFocusConsumed, showImages]);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = needle
      ? models.filter((model) => model.name.toLowerCase().includes(needle))
      : models;
    return rows.slice(0, 20);
  }, [models, query]);

  const loadDatasetImages = async () => {
    const dataset = datasets.find((row) => row.dataset_id === datasetId);
    if (!dataset) {
      setMessage('Select a dataset first');
      return;
    }
    setMessage('');
    const res = await api.dataviewer.listRegistryImages(dataset.name, dataset.version);
    await showImages(res.data.images || []);
  };

  const runInfer = async (imageUrl: string, previewUrl?: string) => {
    if (!modelUri) {
      setMessage('Select a model first');
      return;
    }
    setSelected(imageUrl);
    setPreview(previewUrl || null);
    setLoading(true);
    setMessage('');
    setPredictions([]);
    try {
      const res = await api.dataviewer.inferImage({
        model_s3_uri: modelUri,
        image_url: imageUrl,
      });
      setPredictions(res.data.predictions || []);
    } catch (e: any) {
      setMessage(e.response?.data?.detail || e.message || 'Inference failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Inference</h2>

      <Card title="Model">
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <Field label="Search registry">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Model name"
              style={inputStyle}
            />
          </Field>
          {matches.length === 0 ? (
            <EmptyState>No registry models match that search.</EmptyState>
          ) : (
            <div style={{ display: 'grid', gap: '0.35rem', maxHeight: 220, overflow: 'auto' }}>
              {matches.flatMap((model) => (
                model.versions.map((version) => {
                  const label = `${model.name} v${version.version}`;
                  const active = selectedModel === label;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => chooseVersion(model, version)}
                      style={{
                        textAlign: 'left',
                        padding: '0.4rem 0.65rem',
                        borderRadius: tokens.radius,
                        border: `1px solid ${active ? tokens.primary : tokens.border}`,
                        background: active ? tokens.primarySoft : tokens.surface,
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                      {version.tags.lifecycle_stage ? ` · ${version.tags.lifecycle_stage}` : ''}
                    </button>
                  );
                })
              ))}
            </div>
          )}
          {modelUri ? (
            <p style={{ margin: 0, fontSize: '0.82rem', color: tokens.textMuted }}>
              {selectedModel}. Loaded on CPU for this DataViewer process. Predictions are not ingested.
            </p>
          ) : null}
        </div>
      </Card>

      <Card title="Images">
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'end' }}>
            <Field label="Registry dataset">
              <select value={datasetId} onChange={(e) => setDatasetId(e.target.value)} style={inputStyle}>
                <option value="">Select a dataset</option>
                {datasets.map((row) => (
                  <option key={row.dataset_id} value={row.dataset_id}>{row.name}@{row.version}</option>
                ))}
              </select>
            </Field>
            <Button variant="primary" onClick={() => loadDatasetImages().catch((e) => setMessage(String(e)))}>
              Load images
            </Button>
          </div>
          {message && !selected ? (
            <p style={{ margin: 0, color: tokens.danger, fontSize: '0.85rem' }}>{message}</p>
          ) : null}
          {images.length === 0 ? (
            <EmptyState>Load images from a registry dataset, then click one to run CPU inference.</EmptyState>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.5rem' }}>
              {images.map((image) => (
                <button
                  key={image.file_location}
                  type="button"
                  onClick={() => runInfer(image.file_location, image.preview)}
                  disabled={!modelUri || loading}
                  style={{
                    padding: 0,
                    border: `2px solid ${selected === image.file_location ? tokens.primary : tokens.border}`,
                    borderRadius: tokens.radius,
                    overflow: 'hidden',
                    cursor: !modelUri || loading ? 'not-allowed' : 'pointer',
                    background: tokens.surface,
                  }}
                >
                  {image.preview ? (
                    <img src={image.preview} alt="" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <span style={{ display: 'block', padding: '0.5rem', fontSize: '0.7rem', color: tokens.textFaint }}>
                      {image.file_location.split('/').pop()}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Card title="Overlay">
        {!modelUri ? (
          <EmptyState>Select a model to enable click-to-infer.</EmptyState>
        ) : !selected ? (
          <EmptyState>Click an image to draw boxes on CPU.</EmptyState>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
              <Badge tone={loading ? 'info' : message ? 'danger' : 'success'}>
                {loading ? 'cpu running' : message ? 'error' : 'cpu'}
              </Badge>
              <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>{selected}</span>
            </div>
            {message ? (
              <p style={{ color: tokens.danger, fontSize: '0.85rem' }}>{message}</p>
            ) : null}
            <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
              {preview ? (
                <img src={preview} alt="" style={{ maxWidth: '100%', display: 'block' }} />
              ) : (
                <EmptyState>No preview URL for this image.</EmptyState>
              )}
              {predictions.map((pred, idx) => {
                if (!pred.bbox) return null;
                const { x1, y1, x2, y2 } = pred.bbox;
                return (
                  <div
                    key={idx}
                    style={{
                      position: 'absolute',
                      left: `${x1 * 100}%`,
                      top: `${y1 * 100}%`,
                      width: `${(x2 - x1) * 100}%`,
                      height: `${(y2 - y1) * 100}%`,
                      border: `2px solid ${tokens.primary}`,
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        background: tokens.primary,
                        color: tokens.surface,
                        fontSize: '0.7rem',
                        padding: '0 0.25rem',
                      }}
                    >
                      {pred.class_name} {pred.confidence != null ? pred.confidence.toFixed(2) : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
